import {
  ContentItem,
  ContentItems,
  ContentWrapper,
  Flex,
  Space,
  Text,
  TitledText,
} from '@/components';
import { PinpointMarker } from '@/features/comment/components/pinpoint';
import { getPinpointSceneAndFieldBySheetType } from '@/features/comment/utils/pinpoint';
import { isBefore2025 as isBefore2025Fnc } from '@/features/project/_utils/project';
import { FundSheetModel } from '@/features/project/projects/_models/project';
import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { KsSheetTypeMetaInformation } from './KsSheetTypeMetaInformation';
import { ProjectInformationHeader } from './ProjectInformationHeader';
import {
  KsBusinessFormPinpointItem,
  KsFundNecessityReasonsPinpointItem,
  KsFundNecessityTypePinpointItem,
  KsOperationFormPinpointItem,
  ProjectGroupPinpointItem,
  ProjectNumberPinpointItem,
  ProjectOldProjectNumberPinpointItem,
  ProjectRemarkPinpointItem,
} from './pinpoint-items/basic-information-pinpoint-fields';

type Props = z.infer<typeof FundSheetModel> & {
  historySavedAt: string | undefined;
};

const PINPOINT_SHEET_TYPE = 'KS_LOCAL_GOVERNMENT';

export const KsLocalGovernmentProjectInformation: FC<Props> = (props) => {
  const {
    id,
    fiscalYear,
    projectNumber,
    oldProjectNumber,
    purpose,
    overview,
    task,
    remark,
    name,
    ...rest
  } = FundSheetModel.parse(props);
  const { t } = useTranslation();

  const isBefore2025 = useMemo(
    () => isBefore2025Fnc({ fiscalYear }),
    [fiscalYear],
  );

  return (
    <>
      <ProjectInformationHeader
        ministryId={rest.ministryId}
        historySavedAt={props.historySavedAt}
        organization={rest.organization}
        relatedOrganizations={rest.relatedOrganizations}
        otherResponsibleOrganizations={rest.otherResponsibleOrganizations}
        peopleInCharge={rest.peopleInCharge}
        businessManagers={rest.businessManagers}
        snapshotId={rest.snapshotId}
        pinpointSheetType="KS_LOCAL_GOVERNMENT"
      />

      <ContentWrapper title={t('basic-information')}>
        <ContentItem borderBottom={0}>
          <ContentItems
            items={[
              <ProjectNumberPinpointItem
                key="project-number"
                projectNumber={projectNumber}
                pinpointSheetType={PINPOINT_SHEET_TYPE}
                projectId={id}
              />,
              <ProjectGroupPinpointItem
                key="project-group"
                projectId={id}
                pinpointSheetType={PINPOINT_SHEET_TYPE}
              />,
            ]}
          />
        </ContentItem>

        <Flex vertical gap={4}>
          {rest.fundAdditionalAttributes && (
            <KsSheetTypeMetaInformation
              isBefore2025={isBefore2025}
              fundSheetType={rest.fundAdditionalAttributes.fundSheetType}
              fundSheetNumber={rest.fundAdditionalAttributes.fundSheetNumber}
              fundSheetBranchNumber={
                rest.fundAdditionalAttributes.fundSheetBranchNumber
              }
            />
          )}

          <Flex vertical>
            <Text variant="h4" text={name} weight="bold" />
            <Text
              variant="h5"
              text={rest.fundAdditionalAttributes?.fundBusinessName}
            />
          </Flex>
        </Flex>

        <ContentItem borderBottom={0} paddingBottom={0}>
          <ContentItems
            items={[
              <KsOperationFormPinpointItem
                key="operation-form"
                projectId={id}
                pinpointSheetType={PINPOINT_SHEET_TYPE}
                operationForms={rest.fundAdditionalAttributes?.operationForms}
              />,
              <KsBusinessFormPinpointItem
                key="business-form"
                projectId={id}
                pinpointSheetType={PINPOINT_SHEET_TYPE}
                businessForms={rest.fundAdditionalAttributes?.businessForms}
              />,
            ]}
          />
        </ContentItem>
      </ContentWrapper>

      <ContentWrapper title={t('overview-and-purpose')}>
        <ContentItem borderBottom={0} paddingBottom={0}>
          <ContentItems
            items={[
              <Space key="project-overview" direction="vertical">
                <Text variant="h4" text={t('project-overview')} />
                <PinpointMarker
                  pinpoint={getPinpointSceneAndFieldBySheetType({
                    pinpointSheetType: PINPOINT_SHEET_TYPE,
                    ksLocalGovernment: {
                      field: 'ks.local-government.overview',
                      path: { project: id },
                    },
                  })}
                >
                  <Text variant="body" text={overview} weight="regular" />
                </PinpointMarker>
              </Space>,
            ]}
          />
        </ContentItem>
      </ContentWrapper>

      {!isBefore2025 && (
        <>
          <ContentWrapper title={t('necessity-of-fund-method')}>
            <ContentItem borderBottom={0} paddingBottom={0}>
              <ContentItems
                items={[
                  <KsFundNecessityTypePinpointItem
                    key="fund-necessity-type"
                    projectId={id}
                    pinpointSheetType={PINPOINT_SHEET_TYPE}
                    necessityType={rest.fundAdditionalAttributes?.necessityType}
                  />,
                  <KsFundNecessityReasonsPinpointItem
                    key="fund-necessity-reasons-for-local-government"
                    projectId={id}
                    pinpointSheetType={PINPOINT_SHEET_TYPE}
                    necessityReasons={
                      rest.fundAdditionalAttributes?.necessityReasons
                    }
                  />,
                ]}
              />
            </ContentItem>
          </ContentWrapper>

          <ContentWrapper title={t('scheduled-end-time')}>
            <ContentItem borderBottom={0} paddingBottom={0}>
              <ContentItems
                items={[
                  <PinpointMarker
                    key="fund-scheduled-end-period-for-local-government"
                    pinpoint={getPinpointSceneAndFieldBySheetType({
                      pinpointSheetType: PINPOINT_SHEET_TYPE,
                      ksLocalGovernment: {
                        field:
                          'ks.local-government.fund-scheduled-end-period-for-local-government',
                        path: { project: id },
                      },
                    })}
                  >
                    <TitledText
                      variant="text"
                      title={t(
                        'fund-scheduled-end-period-for-local-government',
                      )}
                      text={
                        rest.fundAdditionalAttributes?.scheduledEnd?.endDate
                      }
                    />
                  </PinpointMarker>,
                  <PinpointMarker
                    key="end-date-of-new-application-for-local-government"
                    pinpoint={getPinpointSceneAndFieldBySheetType({
                      pinpointSheetType: PINPOINT_SHEET_TYPE,
                      ksLocalGovernment: {
                        field:
                          'ks.local-government.end-date-of-new-application-for-local-government',
                        path: { project: id },
                      },
                    })}
                  >
                    <TitledText
                      variant="text"
                      title={t(
                        'end-date-of-new-application-for-local-government',
                      )}
                      text={
                        rest.fundAdditionalAttributes?.scheduledEnd
                          ?.endDateOfNewApplicationReceiving
                      }
                    />
                  </PinpointMarker>,
                ]}
              />
            </ContentItem>
          </ContentWrapper>
        </>
      )}

      <ContentWrapper borderBottom={0} title={t('others')}>
        <ContentItem borderBottom={0} paddingBottom={0}>
          <ContentItems
            items={[
              <PinpointMarker
                key="status-of-response-by-ministries"
                pinpoint={getPinpointSceneAndFieldBySheetType({
                  pinpointSheetType: PINPOINT_SHEET_TYPE,
                  ksLocalGovernment: {
                    field:
                      'ks.local-government.status-of-response-by-ministries',
                    path: { project: id },
                  },
                })}
              >
                <TitledText
                  title={t('status-of-response-by-ministries')}
                  text={task}
                />
              </PinpointMarker>,
            ]}
          />

          <ContentItem borderBottom={0} paddingBottom={0}>
            <ContentItems
              items={[
                <ProjectRemarkPinpointItem
                  key="remark"
                  projectId={id}
                  pinpointSheetType={PINPOINT_SHEET_TYPE}
                  remark={remark}
                />,
                <ProjectOldProjectNumberPinpointItem
                  key="old-project-number"
                  projectId={id}
                  pinpointSheetType={PINPOINT_SHEET_TYPE}
                  oldProjectNumber={oldProjectNumber}
                />,
              ]}
            />
          </ContentItem>
        </ContentItem>
      </ContentWrapper>
    </>
  );
};
